import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { optionalQueryParam } from "../../../public/v1/schemas/common.schemas";

// ---------------------------------------------------------------------------
// Constants (single source of truth — reuse in frontend selects/filters)
// ---------------------------------------------------------------------------

export const CREDENTIAL_SOURCES = [
  "oauth_connection",
  "api_key_connection",
  "tool_credential"
] as const;
export const CREDENTIAL_STATUSES = [
  "CONNECTED",
  "EXPIRING_SOON",
  "NEEDS_REAUTH",
  "DISCONNECTED"
] as const;

// ---------------------------------------------------------------------------
// Shared sub-schemas
// ---------------------------------------------------------------------------

const LinkedDeploymentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string()
});

const CredentialCreatorSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable()
});

export const CredentialItemSchema = z.object({
  id: z.string(),
  source: z.enum(CREDENTIAL_SOURCES),
  service: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  accountIdentifier: z.string().nullable(),
  status: z.enum(CREDENTIAL_STATUSES),
  failureReason: z.string().nullable(),
  /**
   * WHO may spend this credential (NEX-5436).
   *
   * 🔴 THE READ IS WHAT MAKES THE OWNER-GATED WRITE USABLE. `PATCH
   * /credentials/:id/sharing` shipped ahead of this field, so the value was
   * settable and unreadable: `GET /credentials/:id` could not say whether a
   * credential was personal, and the PATCH answered with a body omitting the
   * field it had just changed.
   *
   * Declaring it here is load-bearing rather than cosmetic — Zod STRIPS
   * undeclared keys, so a record carrying `sharing` past a schema without it
   * arrives at the client with the field silently deleted, and `tsc` cannot
   * see that because an excess property survives structural assignment.
   *
   * Not `.optional()`: the column is `NOT NULL DEFAULT 'SHARED'`, so every row
   * has a value and an absent one would be a state no caller can produce.
   *
   * `DbEnum.CREDENTIAL_SHARING_VALUES`, never a hand-written
   * `z.enum(["SHARED", "PERSONAL"])` — same rule the UpdateSharing body states
   * below, and for the same reason: a copy of the fact does not move when the
   * fact does.
   */
  sharing: z.enum(DbEnum.CREDENTIAL_SHARING_VALUES),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  linkedDeployments: z.array(LinkedDeploymentSchema),
  createdBy: CredentialCreatorSchema.nullable(),
  /** Image URL for the service icon (from AgentExternalTool.imageUrl for Pipedream tools) */
  serviceImageUrl: z.string().nullable().optional(),
  /**
   * The external tool this credential is scoped to, or null for the
   * organization-wide sources (NEX-4023).
   *
   * `service` is a display LABEL and two different tools can carry the same
   * one — an organization held two credentials labelled "Apify" scoped to the
   * Pipedream tool *named* Apify while the Apify-type tool that needed one had
   * none, and the flat list read as "already connected". This is the id
   * `GET /tools/:toolId/credentials` takes, so a row can be checked against the
   * tool it is meant to be spent on.
   */
  toolId: z.string().nullable().optional()
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const CREDENTIAL_SORT_FIELDS = ["name", "service", "status", "owner", "createdAt"] as const;

export const CREDENTIAL_SORT_ORDERS = ["asc", "desc"] as const;

export const CredentialListParamsSchema = z.object({
  source: z.enum(CREDENTIAL_SOURCES).optional(),
  service: z.string().optional(),
  status: z.enum(CREDENTIAL_STATUSES).optional(),
  search: z.string().optional(),
  /**
   * Narrow to the credentials scoped to ONE external tool. Answers "is this
   * tool connected" exactly, where `service` answers it only by label — see
   * `CredentialItemSchema.toolId`. Uuid-shaped because `ToolCredentials.externalToolId`
   * is `@db.Uuid`: an unparseable value can only reach Postgres as a failed
   * cast, which is a 500 where a 400 belongs.
   *
   * `optionalQueryParam`, not a bare `.optional()`. This object is parsed from
   * `@Query`, and `qs` turns `?toolId=` into `""` rather than dropping the key —
   * so a bare optional feeds the empty string to the uuid check and 400s a
   * request that meant "no filter". The v1 twin is held to this by
   * `id-validation-consistency.test.ts`; nothing holds this side, which is why
   * the reason is written here rather than assumed from the other file.
   */
  toolId: optionalQueryParam(z.string().uuid()),
  sortBy: z.enum(CREDENTIAL_SORT_FIELDS).optional().default("createdAt"),
  sortOrder: z.enum(CREDENTIAL_SORT_ORDERS).optional().default("desc"),
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50)
});

export const CredentialListMetaSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean()
});

export const CredentialListResponseSchema = z.object({
  credentials: z.array(CredentialItemSchema),
  meta: CredentialListMetaSchema
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const CredentialGetPathVarsSchema = z.object({
  id: z.string()
});

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export const CredentialUpdatePathVarsSchema = z.object({
  id: z.string()
});

/** The longest credential name the update route accepts. */
export const CREDENTIAL_NAME_MAX_LENGTH = 255;
/** The longest credential description the update route accepts. */
export const CREDENTIAL_DESCRIPTION_MAX_LENGTH = 1000;

export const CredentialUpdateBodySchema = z.object({
  name: z.string().trim().min(1).max(CREDENTIAL_NAME_MAX_LENGTH).optional(),
  description: z.string().max(CREDENTIAL_DESCRIPTION_MAX_LENGTH).nullable().optional()
});

// ---------------------------------------------------------------------------
// Update sharing (owner only)
// ---------------------------------------------------------------------------

export const CredentialUpdateSharingPathVarsSchema = z.object({
  id: z.string()
});

/**
 * WHO may spend this credential — the stored fact, set deliberately by its
 * owner.
 *
 * 🔴 DERIVED FROM THE SCHEMA, NEVER RE-TYPED. `DbEnum.CREDENTIAL_SHARING_VALUES`
 * is generated from `enum CredentialSharing` in `schema.prisma`, so a third
 * member added there reaches this contract as a compile-time change rather than
 * as a value the API silently refuses. A hand-written `z.enum(["SHARED",
 * "PERSONAL"])` is a COPY of the fact, and a copy does not move when the fact
 * does.
 *
 * `sharing` is REQUIRED rather than optional: this route exists to change it, so
 * an omitted field is a caller mistake and not a no-op worth accepting.
 */
export const CredentialUpdateSharingBodySchema = z.object({
  sharing: z.enum(DbEnum.CREDENTIAL_SHARING_VALUES)
});

// ---------------------------------------------------------------------------
// Reconnect (OAuth only)
// ---------------------------------------------------------------------------

export const CredentialReconnectPathVarsSchema = z.object({
  id: z.string()
});

export const CredentialReconnectResponseSchema = z.object({
  authorizationUrl: z.string(),
  handshakeId: z.string()
});

// ---------------------------------------------------------------------------
// Secret (API key only — view and edit encrypted values)
// ---------------------------------------------------------------------------

export const CredentialSecretPathVarsSchema = z.object({
  id: z.string()
});

export const CredentialSecretResponseSchema = z.object({
  id: z.string(),
  service: z.string(),
  credentials: z.record(z.string(), z.string())
});

export const CredentialSecretUpdateBodySchema = z.object({
  credentials: z.record(z.string(), z.string())
});

// ---------------------------------------------------------------------------
// Assign owner (admin only)
// ---------------------------------------------------------------------------

export const CredentialAssignOwnerPathVarsSchema = z.object({
  id: z.string()
});

export const CredentialAssignOwnerBodySchema = z.object({
  userId: z.string().min(1)
});

export const CredentialAssignOwnerResponseSchema = CredentialItemSchema;

// ---------------------------------------------------------------------------
// Bulk assign owner (admin only)
// ---------------------------------------------------------------------------

export const CredentialBulkAssignOwnerBodySchema = z.object({
  credentialIds: z.array(z.string().min(1)).min(1).max(100),
  userId: z.string().min(1)
});

export const CredentialBulkAssignOwnerResponseSchema = z.object({
  updated: z.number()
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const CredentialDeletePathVarsSchema = z.object({
  id: z.string()
});

export const CredentialDeleteResponseSchema = z.object({
  deleted: z.literal(true)
});
